import React, { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import { Link } from 'react-router-dom';

function TicketsTable() {

    const [loading, setLoading] = useState(false);
    const [ticketsData, setTicketsData] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [page, setPage] = useState(1);
    const [itemsPerPage] = useState(8);
    const [totalItems, setTotalItems] = useState(0);
    const [ticketsError, setTicketsError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const handleLoad = useCallback(async () => {
        try {
            setLoading(true);
            const response = await authenticatedFetch(`${API_URL}/v1/Tickets`, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Error en la solicitud');
            }
            const data = await response.json();
            setTicketsData(data);
            setTotalItems(data.length);
            setLoading(false);
            setIsLoaded(true);

        } catch (error) {
            console.error('Error:', error);
            setTicketsError(error.message);
        }
    }, []);

    useEffect(() => {
        if (!ticketsData) {
            handleLoad();
        }
    }, [ticketsData, handleLoad]);

    useEffect(() => {
    }, [isLoaded, ticketsData]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Filter tickets based on search term
    const filteredTickets = ticketsData
        ? ticketsData.filter(ticket => {
            if (!searchTerm) return true; // If no search term, show all tickets

            // Search by remitente (user_name)
            const userName = ticket.user_name.toLowerCase();
            return userName.includes(searchTerm.toLowerCase());
        }) 
        : [];

    // Update total items when filtered tickets change
    useEffect(() => {
        if (filteredTickets.length > 0) {
            setTotalItems(filteredTickets.length);
            // If after filtering there are no results on the current page, go back to first page
            if ((page - 1) * itemsPerPage >= filteredTickets.length) {
                setPage(1);
            }
        }
    }, [filteredTickets.length, page, itemsPerPage]);

    const tickets = filteredTickets.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="tickets-table-container">
            {loading && (
                <div className="flex items-center justify-center p-4" data-testid="tickets-table-loading">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-700"></div>
                    <span className="ml-2">Cargando tickets...</span>
                </div>
            )}

            {!loading && (
                <>
                    {/* Search input */}
                    <div className="mt-6 mb-4" data-testid="tickets-search-container">
                        <div className="flex">
                            <div className="flex-1">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setPage(1); // Reset to first page when searching
                                        }}
                                        placeholder="Buscar por nombre..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-colors duration-200"
                                        data-testid="tickets-search-input"
                                    />
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-purple-400 hover:text-purple-600 ${!searchTerm && 'hidden'}`}
                                        data-testid="tickets-search-clear-button"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto shadow-md rounded-lg mt-6" data-testid="tickets-table-wrapper">
                        <table className="min-w-full divide-y divide-gray-300 rounded-lg overflow-hidden" data-testid="tickets-table">
                            <thead className="bg-gray-50" data-testid="tickets-table-header">
                                <tr>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="tickets-table-header-id">ID</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="tickets-table-header-name">Remitente</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell" data-testid="tickets-table-header-date">Fecha</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="tickets-table-header-status">Estado</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="tickets-table-header-actions">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-300" data-testid="tickets-table-body">
                                {tickets.length > 0 ? (
                                    tickets.map((ticket) => (
                                    <tr key={ticket.ticket_id} className="hover:bg-gray-50" data-testid={`tickets-table-row-${ticket.ticket_id}`}>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`tickets-table-cell-id-${ticket.ticket_id}`}>{ticket.ticket_id}</td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`tickets-table-cell-name-${ticket.ticket_id}`}>{ticket.user_name}</td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell" data-testid={`tickets-table-cell-date-${ticket.ticket_id}`}>{formatDate(ticket.create_date)}</td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap" data-testid={`tickets-table-cell-status-${ticket.ticket_id}`}>
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${ticket.status === 1 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                                {ticket.status === 1 ? 'Nuevo' : 'Atendido'}
                                            </span>
                                        </td>
                                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`tickets-table-cell-actions-${ticket.ticket_id}`}>
                                            <Link to={`/Ticket/id/${ticket.ticket_id}`} className="text-amber-700 hover:text-amber-900 font-medium" data-testid={`tickets-table-view-link-${ticket.ticket_id}`}>
                                                Ver
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-3 sm:px-6 py-4 text-center text-sm text-gray-500">
                                            {searchTerm ? (
                                                <div className="py-2">
                                                    <p className="font-medium">No se encontraron tickets que coincidan con "{searchTerm}".</p>
                                                    <p className="text-xs mt-1">Intenta con otro término de búsqueda.</p>
                                                </div>
                                            ) : (
                                                "No se encontraron tickets."
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 mb-4 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="tickets-table-pagination">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            className="w-full sm:w-auto px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="tickets-table-prev-button"
                        >
                            <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                            <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                            <span className="z-10 text-gray-900">Anterior</span>
                        </button>

                        <span className="mx-2 py-1 text-sm text-center" data-testid="tickets-table-page-indicator">
                            Página {page} de {Math.ceil(totalItems / itemsPerPage)}
                        </span>

                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page >= Math.ceil(totalItems / itemsPerPage)}
                            className="w-full sm:w-auto px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="tickets-table-next-button"
                        >
                            <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / itemsPerPage) ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                            <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / itemsPerPage) ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                            <span className="z-10 text-gray-900">Siguiente</span>
                        </button>
                    </div>
                </>
            )}

            {ticketsError && (
                <div className="text-center text-red-600 p-4" data-testid="tickets-table-error">
                    Error al cargar los tickets: {ticketsError}
                </div>
            )}
        </div>
    );
}

export { TicketsTable };
